import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Maximize2, Trash2, User, X } from 'lucide-react';
import { calculateIkrProgress } from '../../lib/utils';

const QUARTER_CODES = ['Q1', 'Q2', 'Q3', 'Q4'];

function getCurrentFY2627QuarterIndex() {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  if (y === 2026 && m >= 4 && m <= 6) return 0;
  if (y === 2026 && m >= 7 && m <= 9) return 1;
  if (y === 2026 && m >= 10 && m <= 12) return 2;
  if (y === 2027 && m >= 1 && m <= 3) return 3;
  return -1;
}

function toNumberOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

function normalizeThread(ikr) {
  const quarters = Array.isArray(ikr.quarters) ? ikr.quarters : [];
  const normalizedQuarters = QUARTER_CODES.map((code, i) => {
    const q = quarters[i] || {};
    return { q: code, text: q.text || '', target: q.target ?? null, actual: q.actual ?? null };
  });

  return {
    ...ikr,
    ownerId: ikr.ownerId || null,
    yearlyText: ikr.yearlyText || normalizedQuarters.find(q => q.text)?.text || '',
    yearlyTarget: ikr.yearlyTarget ?? null,
    kpiUnit: ikr.kpiUnit || '',
    calculationMethod: ikr.calculationMethod || 'sum',
    quarters: normalizedQuarters
  };
}

function quarterProgress(method, target, actual) {
  const t = toNumberOrNull(target);
  const a = toNumberOrNull(actual);
  if (t === null || a === null || t === 0) return null;
  if (method === 'deviation') return a === 0 ? 0 : t / a;
  return a / t;
}

function getStatus(progress) {
  if (progress === null || progress === undefined) return null;
  if (progress >= 1.0) return { label: 'On Track', badge: 'bg-emerald-100 text-emerald-700' };
  if (progress >= 0.7) return { label: 'On Track', badge: 'bg-green-100 text-green-700' };
  if (progress >= 0.3) return { label: 'At Risk', badge: 'bg-amber-100 text-amber-700' };
  return { label: 'Behind', badge: 'bg-red-100 text-red-700' };
}

function MiniBar({ value, colorClass = 'bg-blue-600' }) {
  const pct = Math.min(Math.max((value || 0) * 100, 0), 100);
  return (
    <div className="w-full bg-slate-100 rounded-full h-1.5">
      <div className={`${colorClass} h-1.5 rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function OwnerIKRThreadFY2627({ ikr, onUpdate, users, onDelete, krCode, onOwnerAssigned, viewer }) {
  const [threadState, setThreadState] = useState(normalizeThread(ikr));
  const [isExpanded, setIsExpanded] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [isOwnerListVisible, setIsOwnerListVisible] = useState(false);
  const [isTextPreviewOpen, setIsTextPreviewOpen] = useState(false);
  const [quarterPreview, setQuarterPreview] = useState({ open: false, qLabel: '', text: '' });

  const canEdit = viewer?.role === 'Admin';
  const currentQIdx = getCurrentFY2627QuarterIndex();

  useEffect(() => { setThreadState(normalizeThread(ikr)); }, [ikr]);
  useEffect(() => {
    const owner = users.find(u => (u.uid || u.id) === ikr.ownerId);
    setOwnerSearch(owner ? owner.name : '');
  }, [ikr.ownerId, users]);

  const filteredUsers = useMemo(() => {
    if (!ownerSearch) return users;
    return users.filter(u => u.name?.toLowerCase().includes(ownerSearch.toLowerCase()));
  }, [ownerSearch, users]);

  const overallProgress = calculateIkrProgress(threadState);
  const status = getStatus(overallProgress);
  const ownerName = users.find(u => (u.uid || u.id) === threadState.ownerId)?.name;

  const persist = (nextState) => {
    setThreadState(nextState);
    onUpdate(ikr.id, nextState);
  };

  const updateQuarterField = (index, field, value) => {
    const nextQuarters = [...threadState.quarters];
    nextQuarters[index] = { ...nextQuarters[index], [field]: value };
    setThreadState(prev => ({ ...prev, quarters: nextQuarters }));
  };

  const handleOwnerSelect = (user) => {
    setOwnerSearch(user.name);
    const ownerId = user.uid || user.id;
    const nextState = { ...threadState, ownerId };
    persist(nextState);
    setIsOwnerListVisible(false);
    if (onOwnerAssigned) onOwnerAssigned(ownerId, krCode);
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white mb-2 overflow-visible">
      <div className="p-4 flex items-start justify-between gap-4 border-b border-slate-100">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-slate-400 hover:text-blue-600 mt-0.5"
            title={isExpanded ? 'Collapse quarters' : 'Expand quarters'}
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <User size={13} className="text-slate-400" />
              {canEdit ? (
                <div className="relative">
                  <input
                    type="text"
                    value={ownerSearch}
                    onChange={(e) => { setOwnerSearch(e.target.value); setIsOwnerListVisible(true); }}
                    onFocus={() => setIsOwnerListVisible(true)}
                    onBlur={() => setTimeout(() => setIsOwnerListVisible(false), 200)}
                    placeholder="Owner"
                    className="text-sm font-semibold text-slate-800 bg-transparent border-b border-dashed border-slate-300 focus:border-blue-500 p-0 outline-none w-44"
                  />
                  {isOwnerListVisible && filteredUsers.length > 0 && (
                    <ul className="absolute z-30 bg-white border border-slate-200 rounded-lg mt-1 w-56 max-h-48 overflow-y-auto shadow-xl">
                      {filteredUsers.map(user => (
                        <li
                          key={user.uid || user.id}
                          onMouseDown={() => handleOwnerSelect(user)}
                          className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                        >
                          {user.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <span className="text-sm font-semibold text-slate-800">{ownerName || 'Unassigned'}</span>
              )}
            </div>

            {canEdit ? (
              <div className="relative">
                <input
                  type="text"
                  value={threadState.yearlyText || ''}
                  onChange={(e) => setThreadState(prev => ({ ...prev, yearlyText: e.target.value }))}
                  onBlur={() => persist(threadState)}
                  placeholder="Yearly Individual Key Result"
                  className="w-full pr-8 text-sm bg-slate-50 border border-slate-200 rounded-md px-2 py-1 focus:ring-1 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setIsTextPreviewOpen(true)}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600"
                  title="View full IKR text"
                >
                  <Maximize2 size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsTextPreviewOpen(true)}
                className="w-full text-left text-sm text-slate-700 truncate hover:text-blue-700"
                title="Click to view full text"
              >
                {threadState.yearlyText || 'No yearly IKR text'}
              </button>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-2">
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500 uppercase font-semibold">Target</span>
                {canEdit ? (
                  <input
                    type="number"
                    value={threadState.yearlyTarget ?? ''}
                    onChange={(e) => setThreadState(prev => ({ ...prev, yearlyTarget: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                    onBlur={() => persist(threadState)}
                    className="w-20 px-2 py-0.5 border border-slate-200 rounded text-xs font-semibold text-right"
                    placeholder="—"
                  />
                ) : (
                  <span className="text-xs font-bold text-slate-700">{threadState.yearlyTarget ?? '—'}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500 uppercase font-semibold">Unit</span>
                {canEdit ? (
                  <input
                    type="text"
                    value={threadState.kpiUnit}
                    onChange={(e) => setThreadState(prev => ({ ...prev, kpiUnit: e.target.value }))}
                    onBlur={() => persist(threadState)}
                    className="w-14 px-2 py-0.5 border border-slate-200 rounded text-xs font-semibold text-center"
                    placeholder="—"
                  />
                ) : (
                  <span className="text-xs font-bold text-slate-700">{threadState.kpiUnit || '—'}</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500 uppercase font-semibold">Method</span>
                {canEdit ? (
                  <select
                    value={threadState.calculationMethod}
                    onChange={(e) => persist({ ...threadState, calculationMethod: e.target.value })}
                    className="px-2 py-0.5 border border-slate-200 rounded text-xs font-semibold"
                  >
                    <option value="sum">Sum</option>
                    <option value="average">Average</option>
                    <option value="deviation">Deviation</option>
                  </select>
                ) : (
                  <span className="text-xs font-bold text-slate-700 capitalize">{threadState.calculationMethod}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="w-44 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${status?.badge || 'bg-slate-100 text-slate-600'}`}>
              {status?.label || 'No Status'}
            </span>
            {canEdit && (
              <button
                type="button"
                onClick={() => onDelete(ikr.id)}
                className="text-slate-300 hover:text-red-500 p-0.5 rounded hover:bg-red-50"
                title="Delete owner thread"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-slate-500 uppercase">Progress</span>
            <span className="text-[10px] font-bold text-blue-600">{Math.round(overallProgress * 100)}%</span>
          </div>
          <MiniBar value={overallProgress} colorClass="bg-blue-600" />
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 bg-slate-50/60">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {threadState.quarters.map((q, i) => {
              const qProg = quarterProgress(threadState.calculationMethod, q.target, q.actual);
              const isCurrent = i === currentQIdx;

              return (
                <div
                  key={q.q}
                  className={`rounded-lg p-3 border ${isCurrent ? 'border-blue-200 bg-blue-50/50 ring-1 ring-blue-200' : 'border-slate-200 bg-white'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-slate-600 uppercase">{q.q}</span>
                    <button
                      type="button"
                      onClick={() => setQuarterPreview({ open: true, qLabel: q.q, text: q.text || '' })}
                      className="text-slate-400 hover:text-blue-600"
                      title={`View full ${q.q} IKR text`}
                    >
                      <Maximize2 size={12} />
                    </button>
                  </div>
                  <div className="mb-2">
                    {qProg !== null && <span className="text-[11px] font-bold text-slate-700">{Math.round(qProg * 100)}%</span>}
                  </div>

                  {canEdit ? (
                    <textarea
                      rows={2}
                      value={q.text}
                      onChange={(e) => updateQuarterField(i, 'text', e.target.value)}
                      onBlur={() => persist(threadState)}
                      placeholder={`${q.q} Individual KR`}
                      className="w-full text-xs p-2 border border-slate-200 rounded resize-none mb-2"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setQuarterPreview({ open: true, qLabel: q.q, text: q.text || '' })}
                      className="w-full text-left text-xs text-slate-600 mb-2 min-h-[34px] hover:text-blue-700"
                      title={`View full ${q.q} IKR text`}
                    >
                      {q.text || 'No quarter text'}
                    </button>
                  )}

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-slate-500 uppercase font-semibold">Target</span>
                      {canEdit ? (
                        <input
                          type="number"
                          value={q.target ?? ''}
                          onChange={(e) => updateQuarterField(i, 'target', e.target.value === '' ? null : parseFloat(e.target.value))}
                          onBlur={() => persist(threadState)}
                          className="w-20 px-1.5 py-0.5 border border-slate-200 rounded text-[11px] font-semibold text-right"
                          placeholder="—"
                        />
                      ) : (
                        <span className="text-[11px] font-semibold text-slate-700">{q.target ?? '—'}</span>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] text-slate-500 uppercase font-semibold">Actual</span>
                      <input
                        type="number"
                        value={q.actual ?? ''}
                        onChange={(e) => updateQuarterField(i, 'actual', e.target.value === '' ? null : parseFloat(e.target.value))}
                        onBlur={() => persist(threadState)}
                        className="w-20 px-1.5 py-0.5 border border-blue-200 rounded text-[11px] font-semibold text-right"
                        placeholder="—"
                      />
                    </div>

                    {qProg !== null && <MiniBar value={qProg} colorClass="bg-blue-500" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isTextPreviewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h4 className="text-sm font-bold text-slate-800">Individual Key Result</h4>
              <button
                type="button"
                onClick={() => setIsTextPreviewOpen(false)}
                className="text-slate-400 hover:text-slate-700"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-4 py-3 max-h-[55vh] overflow-y-auto">
              <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                {threadState.yearlyText || 'No text entered yet.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {quarterPreview.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl border border-slate-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <h4 className="text-sm font-bold text-slate-800">{quarterPreview.qLabel} Individual Key Result</h4>
              <button
                type="button"
                onClick={() => setQuarterPreview({ open: false, qLabel: '', text: '' })}
                className="text-slate-400 hover:text-slate-700"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-4 py-3 max-h-[55vh] overflow-y-auto">
              <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                {quarterPreview.text || 'No text entered yet.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
